import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {Confirmation, ConfirmationService} from 'primeng/api';
import {ConfirmDialogService} from '../service/confirm-dialog.service';
import {ConfirmDialogConfig} from '../model/confirm-dialog.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-confirm-dialog',
  imports: [ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './confirm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ConfirmDialogComponent implements OnInit {
  protected readonly severity = signal<ConfirmDialogConfig['severity']>(undefined);
  protected readonly style = signal<{ [klass: string]: any } | undefined>(undefined);
  protected readonly styleClass = signal<string | undefined>(undefined);
  protected readonly maskStyleClass = signal<string | undefined>(undefined);
  protected readonly dismissableMask = signal<boolean>(true);
  protected readonly closeAriaLabel = signal<string | undefined>(undefined);
  protected readonly acceptAriaLabel = signal<string | undefined>(undefined);
  protected readonly rejectAriaLabel = signal<string | undefined>(undefined);
  protected readonly appendTo = signal<any>('body');
  protected readonly autoZIndex = signal<boolean>(true);
  protected readonly baseZIndex = signal<number>(999);
  protected readonly transitionOptions = signal<string>('150ms cubic-bezier(0, 0, 0.2, 1)');
  protected readonly focusTrap = signal<boolean>(false);
  protected readonly breakpoints = signal<{ [klass: string]: any } | undefined>(undefined);
  protected readonly draggable = signal<boolean | undefined>(undefined);
  protected readonly defaultFocus = signal<'accept' | 'reject' | 'close' | 'none'>('none');

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
  }

  ngOnInit(): void {
    this.confirmDialogService.config$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config) => {
        this.setInputOnlySignals(config);
        this.confirmationService.confirm(this.toConfirmation(config));
        this.cdr.detectChanges();
      });
  }

  private setInputOnlySignals(c: ConfirmDialogConfig): void {
    this.severity.set(c.severity);
    this.style.set(c.style);
    this.styleClass.set(c.styleClass);
    this.maskStyleClass.set(c.maskStyleClass);
    this.closeAriaLabel.set(c.closeAriaLabel);
    this.acceptAriaLabel.set(c.acceptAriaLabel);
    this.rejectAriaLabel.set(c.rejectAriaLabel);
    this.appendTo.set(c.appendTo ?? 'body');
    !c.autoZIndex || this.autoZIndex.set(c.autoZIndex);
    !c.baseZIndex || this.baseZIndex.set(c.baseZIndex);
    !c.transitionOptions || this.transitionOptions.set(c.transitionOptions);
    !c.focusTrap || this.focusTrap.set(c.focusTrap);
    this.breakpoints.set(c.breakpoints);
    this.draggable.set(c.draggable);
    !c.dismissableMask || this.dismissableMask.set(c.dismissableMask);
    !c.defaultFocus || this.defaultFocus.set(c.defaultFocus)
  }

  private toConfirmation(c: ConfirmDialogConfig): Confirmation {
    return {
      message: c.message,
      header: c.header,
      icon: c.icon,
      key: c.key,

      accept: c.accept,
      reject: c.reject,

      acceptButtonProps: c.acceptButtonProps,
      rejectButtonProps: c.rejectButtonProps,
      closeButtonProps: c.closeButtonProps,

      closeOnEscape: c.closeOnEscape,
      dismissableMask: c.dismissableMask,
      blockScroll: c.blockScroll,
      closable: c.closable,
      defaultFocus: c.defaultFocus,
      position: c.position,
      modal: c.modal,
    };
  }
}
